import React from 'react';
import { useLang } from '@/i18n/LanguageContext';
import { Container, SectionHeading, SectionKicker, TricolourRibbon } from '@/components/common/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { NGRP_LOGO_URL, NGRP_EMBLEM_PANEL_URL } from '@/lib/config';
import {
  Sunrise, Cog, Building2, BookOpen, Users, Compass, Leaf, Info, ExternalLink,
  Target, HandHeart, Megaphone,
} from 'lucide-react';
import {
  EMBLEM_INTRO,
  EMBLEM_ELEMENTS,
  EMBLEM_PILLARS,
  EMBLEM_COMMUNICATES,
  EMBLEM_COLOURS,
  EMBLEM_MISSION_TRIO,
  EMBLEM_MESSAGE,
  EMBLEM_ECI_NOTICE,
  EMBLEM_LABELS,
  pickLang,
} from '@/data/emblemContent';

const ICON_MAP = {
  Sunrise, Cog, Building2, BookOpen, Users, Compass, Leaf,
};

export default function EmblemPage() {
  const { lang } = useLang();
  const intro = pickLang(EMBLEM_INTRO, lang);
  const elements = pickLang(EMBLEM_ELEMENTS, lang);
  const pillars = pickLang(EMBLEM_PILLARS, lang);
  const communicates = pickLang(EMBLEM_COMMUNICATES, lang);
  const colours = pickLang(EMBLEM_COLOURS, lang);
  const trio = pickLang(EMBLEM_MISSION_TRIO, lang);
  const message = pickLang(EMBLEM_MESSAGE, lang);
  const eci = pickLang(EMBLEM_ECI_NOTICE, lang);
  const L = pickLang(EMBLEM_LABELS, lang);

  return (
    <div>
      {/* HERO ------------------------------------------------------------- */}
      <section className="hero-surface">
        <Container className="py-14 sm:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6 order-2 lg:order-1">
              <SectionKicker>{intro.kicker}</SectionKicker>
              <h1
                className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#0B1220] tracking-[-0.01em]"
                data-testid="emblem-hero-title"
              >
                {intro.title}
              </h1>
              <p className="mt-5 max-w-2xl text-[#5B6B82] text-base sm:text-lg leading-relaxed">
                {intro.body}
              </p>
              <div
                className="mt-6 inline-flex items-center rounded-full border border-[#D7E1EF] bg-white px-3 py-1.5 text-xs font-medium text-[#062B5C]"
                data-testid="emblem-motto"
              >
                <span className="tricolour-dot" />
                {intro.motto}
              </div>
            </div>

            <div className="lg:col-span-6 order-1 lg:order-2">
              <div className="relative">
                <div
                  className="absolute -inset-4 rounded-2xl bg-gradient-to-br from-[#F47B20]/10 via-white to-[#138A4B]/10 blur-2xl"
                  aria-hidden="true"
                />
                <div className="relative bg-white rounded-2xl p-3 sm:p-4 shadow-lg border border-[#D7E1EF]">
                  <img
                    src={NGRP_EMBLEM_PANEL_URL}
                    alt={intro.panel_alt}
                    width={1254}
                    height={1254}
                    loading="eager"
                    decoding="async"
                    fetchPriority="high"
                    className="w-full h-auto rounded-xl object-contain"
                    data-testid="emblem-hero-panel"
                  />
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* 7 SYMBOL ELEMENTS ------------------------------------------------- */}
      <section className="py-16 bg-white border-y border-[#D7E1EF]">
        <Container>
          <SectionHeading
            eyebrow={L.elements_kicker}
            title={L.elements_title}
            subtitle={L.elements_sub}
          />
          <div
            className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
            data-testid="emblem-elements-grid"
          >
            {elements.map((el) => {
              const Icon = ICON_MAP[el.icon] || Info;
              return (
                <Card
                  key={el.key}
                  className="border-[#D7E1EF] bg-white h-full transition-shadow hover:shadow-sm"
                  data-testid="emblem-element-card"
                >
                  <CardContent className="p-5 h-full flex flex-col">
                    <div
                      className="h-11 w-11 rounded-xl bg-[#F5F8FC] border border-[#D7E1EF] flex items-center justify-center"
                      style={{ color: el.tone }}
                      aria-hidden="true"
                    >
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="mt-4 font-display text-lg font-semibold text-[#0B1220]">
                      {el.title}
                    </div>
                    <div
                      className="mt-1 text-[11px] font-semibold uppercase tracking-[0.14em]"
                      style={{ color: el.tone }}
                    >
                      {el.subtitle}
                    </div>
                    <p className="mt-3 text-sm text-[#5B6B82] leading-relaxed flex-1">
                      {el.body}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </Container>
      </section>

      {/* WHAT THE LOGO COMMUNICATES ---------------------------------------- */}
      <section className="py-16">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <SectionKicker>{L.communicates_kicker}</SectionKicker>
              <h2 className="font-display mt-3 text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#0B1220] tracking-[-0.01em]">
                {L.communicates_title}
              </h2>
              <p className="mt-4 text-[15px] sm:text-base text-[#5B6B82] leading-relaxed">
                {L.communicates_lede}
              </p>

              <div className="mt-6 flex items-center gap-3">
                <img
                  src={NGRP_LOGO_URL}
                  alt="NGRP emblem mark"
                  width={96}
                  height={96}
                  loading="lazy"
                  decoding="async"
                  className="w-16 h-16 rounded-full object-contain border border-[#D7E1EF] bg-white p-1"
                  data-testid="emblem-mark-inline"
                />
                <div className="text-xs text-[#5B6B82] leading-relaxed max-w-[220px]">
                  {L.mark_caption}
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              <ul
                className="space-y-3"
                data-testid="emblem-communicates-list"
              >
                {communicates.map((item, idx) => (
                  <li
                    key={idx}
                    className="flex items-start gap-4 rounded-xl border border-[#D7E1EF] bg-white p-4 sm:p-5"
                    data-testid="emblem-communicates-item"
                  >
                    <span
                      className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#EAF6EF] border border-[#138A4B]/25 text-[#138A4B] font-semibold text-xs"
                      aria-hidden="true"
                    >
                      {idx + 1}
                    </span>
                    <div>
                      <div className="font-display text-base sm:text-lg font-semibold text-[#0B1220]">
                        {item.title}
                      </div>
                      <p className="mt-1 text-sm text-[#5B6B82] leading-relaxed">
                        {item.body}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Container>
      </section>

      {/* COLOURS & MEANING ------------------------------------------------- */}
      <section className="py-16 bg-[#F5F8FC] border-y border-[#D7E1EF]">
        <Container>
          <SectionHeading
            eyebrow={L.colours_kicker}
            title={L.colours_title}
            subtitle={L.colours_sub}
          />
          <div
            className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5"
            data-testid="emblem-colours-grid"
          >
            {colours.map((c) => (
              <div
                key={c.key}
                className="rounded-xl border border-[#D7E1EF] bg-white overflow-hidden"
                data-testid="emblem-colour-card"
              >
                <div
                  className="h-24 relative"
                  style={{ backgroundColor: c.swatch }}
                  aria-hidden="true"
                >
                  {c.key === 'white' && (
                    <div className="absolute inset-0 border border-[#D7E1EF] rounded-t-xl" />
                  )}
                </div>
                <div className="p-4 sm:p-5">
                  <div className="font-display text-lg font-semibold text-[#0B1220]">
                    {c.title}
                  </div>
                  <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#5B6B82]">
                    {c.swatch}
                  </div>
                  <p className="mt-3 text-sm text-[#5B6B82] leading-relaxed">
                    {c.body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* FIVE PILLARS ------------------------------------------------------ */}
      <section className="py-16">
        <Container>
          <SectionHeading
            eyebrow={L.pillars_kicker}
            title={L.pillars_title}
            subtitle={L.pillars_sub}
          />
          <div
            className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4"
            data-testid="emblem-pillars-grid"
          >
            {pillars.map((p) => (
              <div
                key={p.key}
                className="rounded-xl border border-[#D7E1EF] bg-white p-5 flex flex-col"
                data-testid="emblem-pillar-card"
              >
                <div
                  className="text-[10px] font-semibold uppercase tracking-[0.18em]"
                  style={{ color: p.tone }}
                >
                  {L.pillar_label}
                </div>
                <div
                  className="mt-2 font-display text-xl font-semibold"
                  style={{ color: p.tone }}
                  lang="hi"
                >
                  {p.local}
                </div>
                <div className="mt-1 text-sm font-medium text-[#0B1220]">
                  {p.title}
                </div>
                <div className="mt-3 text-sm text-[#5B6B82] leading-relaxed flex-1">
                  {p.line}
                </div>
              </div>
            ))}
          </div>

          {/* Overall message */}
          <div
            className="mt-12 rounded-2xl border border-[#D7E1EF] bg-[#062B5C] p-8 text-white"
            data-testid="emblem-message-card"
          >
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-white/80">
              {L.overall_label}
            </div>
            <p className="mt-3 font-display text-xl sm:text-2xl leading-snug text-white">
              {message}
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {['Constitution', 'Knowledge', 'Technology', 'Accountability', 'Prosperity'].map((w) => (
                <span
                  key={w}
                  className="inline-flex items-center rounded-full bg-white/10 text-white text-xs font-medium px-3 py-1.5 border border-white/15"
                >
                  {w}
                </span>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* PURPOSE / PROMISE / SLOGAN ---------------------------------------- */}
      <section className="py-16 bg-white border-y border-[#D7E1EF]">
        <Container>
          <SectionHeading
            eyebrow={L.mission_kicker}
            title={L.mission_title}
            subtitle={L.mission_sub}
          />
          <div
            className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5"
            data-testid="emblem-mission-grid"
          >
            <MissionCard
              icon={Target}
              tone="#138A4B"
              title={trio.purpose.title}
              body={trio.purpose.body}
              testid="emblem-purpose-card"
            />
            <MissionCard
              icon={HandHeart}
              tone="#062B5C"
              title={trio.promise.title}
              body={trio.promise.body}
              testid="emblem-promise-card"
            />
            <MissionCard
              icon={Megaphone}
              tone="#F47B20"
              title={trio.slogan.title}
              body={trio.slogan.body}
              testid="emblem-slogan-card"
              emphasize
            />
          </div>
        </Container>
      </section>

      {/* ECI DISCLAIMER ---------------------------------------------------- */}
      <section className="py-14 bg-[#F5F8FC] border-y border-[#D7E1EF]">
        <Container>
          <Card className="border-[#D7E1EF] bg-white" data-testid="emblem-eci-notice">
            <CardContent className="p-6 sm:p-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
              <div className="lg:col-span-1 flex lg:justify-center">
                <div className="h-11 w-11 rounded-xl bg-[#FFF1E8] border border-[#F47B20]/30 flex items-center justify-center text-[#F47B20]">
                  <Info className="h-5 w-5" />
                </div>
              </div>
              <div className="lg:col-span-11">
                <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#062B5C]">
                  {L.important_label}
                </div>
                <h3 className="mt-2 font-display text-lg sm:text-xl font-semibold text-[#0B1220]">
                  {eci.heading}
                </h3>
                <p className="mt-3 text-sm text-[#0B1220] leading-relaxed max-w-4xl">
                  {eci.body}
                </p>
                <a
                  href={eci.link_href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-[#062B5C] hover:text-[#05244d]"
                  data-testid="emblem-eci-link"
                >
                  {eci.link_label}
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </div>
            </CardContent>
          </Card>
        </Container>
      </section>

      <TricolourRibbon />
    </div>
  );
}

function MissionCard({ icon: Icon, tone, title, body, testid, emphasize }) {
  return (
    <div
      className={`rounded-xl border p-6 h-full flex flex-col ${
        emphasize
          ? 'bg-[#062B5C] border-[#062B5C] text-white'
          : 'bg-white border-[#D7E1EF] text-[#0B1220]'
      }`}
      data-testid={testid}
    >
      <div
        className={`h-10 w-10 rounded-lg flex items-center justify-center ${
          emphasize ? 'bg-white/10 border border-white/20' : 'bg-[#F5F8FC] border border-[#D7E1EF]'
        }`}
        style={emphasize ? { color: '#F47B20' } : { color: tone }}
        aria-hidden="true"
      >
        <Icon className="h-5 w-5" />
      </div>
      <div
        className={`mt-4 text-xs font-semibold uppercase tracking-[0.14em] ${
          emphasize ? 'text-white/80' : ''
        }`}
        style={!emphasize ? { color: tone } : undefined}
      >
        {title}
      </div>
      <p
        className={`mt-2 font-display text-base sm:text-lg leading-snug ${
          emphasize ? 'text-white font-semibold' : 'text-[#0B1220] font-medium'
        } flex-1`}
      >
        {body}
      </p>
    </div>
  );
}
